import type { LoadLink } from "../services/calculationRun";
import { formatQuantity } from "../utils/formatQuantity";
import { StatusIcon } from "./StatusIcon";

// The load run a stored power quality run read its demand figures from (EOS-11, A32) in the
// shared UX-1 contracts, beside the SLD and fault link panels: the banner names the run and
// its revision, the line gives the demand kW, kVA and the power factor the engine used with
// the exact decimals in its tooltip (A12), and a REVIEW outcome lists one review-tone card
// per warning. Nothing is shown for a study that named no load run.

export const LOAD_LINK_CODE_LABELS: Record<string, string> = {
  LOAD_RUN_NOT_VALID: "Load run under review",
};

export function loadLinkStatusText(link: LoadLink): string {
  const run = `${link.calculation_key} rev ${link.revision_number}`;
  return link.status === "VALID"
    ? `Demand figures from load run ${run}: valid`
    : `Demand figures from load run ${run}: review required`;
}

/** "P 400.0 kW · S 500.0 kVA · Q 300.0 kvar · PF 0.8" */
export function loadLinkDetailText(link: LoadLink): string {
  const p = formatQuantity(link.demand_power_kw, "kW").display;
  const s = formatQuantity(link.apparent_power_kva, "kVA").display;
  const q = formatQuantity(link.reactive_power_kvar, "kvar").display;
  const pf = formatQuantity(link.power_factor, "").display.trim();
  return `P ${p} · S ${s} · Q ${q} · PF ${pf}`;
}

/** The exact stored decimals for the tooltip. */
export function loadLinkDetailTitle(link: LoadLink): string {
  return [
    formatQuantity(link.demand_power_kw, "kW").exact,
    formatQuantity(link.apparent_power_kva, "kVA").exact,
    formatQuantity(link.reactive_power_kvar, "kvar").exact,
    `PF ${link.power_factor}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

type LoadLinkPanelProps = {
  link: LoadLink | null | undefined;
};

export function LoadLinkPanel({ link }: LoadLinkPanelProps) {
  if (!link) {
    return null;
  }
  const valid = link.status === "VALID";
  return (
    <section aria-label="Load figures source" data-summary-tone={valid ? "pass" : "warning"}>
      <header>
        <p data-summary-status={link.status}>
          <StatusIcon tone={valid ? "pass" : "review"} size={20} />
          {loadLinkStatusText(link)}
        </p>
        <p data-load-link-figures={link.load_run_id} title={loadLinkDetailTitle(link)}>
          {loadLinkDetailText(link)}
          {valid
            ? " · the load run's own result is valid."
            : ` · ${link.warnings.length} item(s) to review.`}
        </p>
      </header>
      {link.warnings.length > 0 ? (
        <ul data-warning-count={link.warnings.length}>
          {link.warnings.map((warning, index) => (
            <li
              key={`${warning.code}-${index}`}
              data-warning-code={warning.code}
              data-warning-tone="review"
            >
              <strong>
                <StatusIcon tone="review" size={16} />
                {LOAD_LINK_CODE_LABELS[warning.code] ?? warning.code}
              </strong>
              {": "}
              <span>{warning.message}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export default LoadLinkPanel;
